import { useState } from "react";
import { 
  Card, 
  CardContent, 
  CardDescription, 
  CardFooter, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  CheckCircleIcon, 
  XCircleIcon, 
  AlertCircleIcon, 
  ArrowUpCircleIcon, 
  ArrowDownCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  FileTextIcon
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";
import { RFQComparison } from "@/lib/rfq-service";
import { formatCurrency } from "@/lib/utils";

interface RFQComparisonProps {
  comparison: RFQComparison;
  onSelectResponse: (responseId: string) => void;
}

export function RFQComparisonTable({ comparison, onSelectResponse }: RFQComparisonProps) {
  const [expandedResponseId, setExpandedResponseId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  
  const { rfq, responses } = comparison;
  
  // Format dates
  const formatDate = (dateString?: string) => {
    if (!dateString) return "Not specified";
    return new Date(dateString).toLocaleDateString();
  };
  
  // Toggle expanded state for a response
  const toggleExpand = (responseId: string) => {
    setExpandedResponseId(
      expandedResponseId === responseId ? null : responseId
    );
  };
  
  // Get color based on score
  const getScoreColor = (score: number) => {
    if (score >= 85) return "text-green-600";
    if (score >= 70) return "text-yellow-600";
    return "text-red-600";
  };
  
  // Get badge for supplier status
  const getStatusBadge = (status: string) => {
    switch (status) {
      case "submitted":
        return <Badge className="bg-blue-500">Submitted</Badge>;
      case "accepted":
        return <Badge className="bg-green-500">Accepted</Badge>;
      case "rejected":
        return <Badge className="bg-red-500">Rejected</Badge>;
      case "negotiating":
        return <Badge className="bg-yellow-500">Negotiating</Badge>;
      default:
        return <Badge className="bg-gray-500">Draft</Badge>;
    }
  };
  
  // If no responses, show a message
  if (responses.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Responses Yet</CardTitle>
          <CardDescription>
            Your RFQ has not received any responses yet. Please check back later.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  // Sort specs for consistent display
  const allSpecKeys = new Set<string>();
  
  // Extract all specification keys from the RFQ and all responses
  const rfqSpecs = rfq.specifications || {};
  Object.keys(rfqSpecs).forEach(key => allSpecKeys.add(key));
  
  responses.forEach(response => {
    const responseSpecs = response.specifications_compliance || {};
    Object.keys(responseSpecs).forEach(key => allSpecKeys.add(key));
  });
  
  const sortedSpecKeys = Array.from(allSpecKeys).sort();
  
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Quote Comparison</CardTitle>
          <CardDescription>
            Compare quotes from {responses.length} suppliers for your request "{rfq.title}"
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="specifications">Specifications</TabsTrigger>
              <TabsTrigger value="pricing">Pricing</TabsTrigger>
              <TabsTrigger value="delivery">Delivery</TabsTrigger>
            </TabsList>
            
            {/* Overview Tab */}
            <TabsContent value="overview" className="space-y-4">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Supplier</TableHead>
                      <TableHead>Overall Score</TableHead>
                      <TableHead>Price</TableHead>
                      <TableHead>Delivery Date</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {responses.map((response) => (
                      <TableRow key={response.id}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Avatar className="h-8 w-8">
                              {response.supplier.logo ? (
                                <AvatarImage src={response.supplier.logo} alt={response.supplier.name || 'Supplier'} />
                              ) : (
                                <AvatarFallback>
                                  {(response.supplier.name && typeof response.supplier.name === 'string') 
                                    ? response.supplier.name.charAt(0) 
                                    : 'S'}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div>
                              <div className="font-medium">{response.supplier.name}</div>
                              {response.supplier.rating && (
                                <div className="text-sm text-muted-foreground">
                                  Rating: {response.supplier.rating}/5
                                </div>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col items-center">
                            <div className={`text-xl font-bold ${getScoreColor(response.overallScore || 0)}`}>
                              {response.overallScore}%
                            </div>
                            <Progress 
                              value={response.overallScore} 
                              className="h-2 w-24"
                              indicatorClassName={
                                response.overallScore && response.overallScore >= 85
                                  ? "bg-green-500"
                                  : response.overallScore && response.overallScore >= 70
                                  ? "bg-yellow-500"
                                  : "bg-red-500"
                              }
                            />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {formatCurrency(response.quoted_price, response.currency)}
                          </div>
                          {response.priceScore !== undefined && (
                            <div className="text-sm text-muted-foreground">
                              Price score: {response.priceScore}%
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>{formatDate(response.delivery_date)}</div>
                          {response.deliveryScore !== undefined && (
                            <div className="text-sm text-muted-foreground">
                              Delivery score: {response.deliveryScore}%
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => toggleExpand(response.id!)}
                            >
                              {expandedResponseId === response.id ? (
                                <ChevronUpIcon className="h-4 w-4 mr-1" />
                              ) : (
                                <ChevronDownIcon className="h-4 w-4 mr-1" />
                              )}
                              Details
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => onSelectResponse(response.id!)}
                            >
                              Select
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              
              {/* Expanded Details */}
              {expandedResponseId && (
                <Card className="mt-4">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg">Detailed Quote Information</CardTitle>
                    <CardDescription>
                      Supplier: {responses.find(r => r.id === expandedResponseId)?.supplier.name}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {responses.filter(r => r.id === expandedResponseId).map(response => (
                      <div key={response.id} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <h4 className="text-sm font-medium mb-1">Status</h4>
                            <div>{getStatusBadge(response.status)}</div>
                          </div>
                          
                          {response.warranty_info && (
                            <div>
                              <h4 className="text-sm font-medium mb-1">Warranty</h4>
                              <div>{response.warranty_info}</div>
                            </div>
                          )}
                          
                          {response.payment_terms && (
                            <div>
                              <h4 className="text-sm font-medium mb-1">Payment Terms</h4>
                              <div>{response.payment_terms}</div>
                            </div>
                          )}
                          
                          {response.validity_period && (
                            <div>
                              <h4 className="text-sm font-medium mb-1">Quote Valid Until</h4>
                              <div>{response.validity_period}</div>
                            </div>
                          )}
                        </div>
                        
                        {response.notes && (
                          <div>
                            <h4 className="text-sm font-medium mb-1">Additional Notes</h4>
                            <div className="text-sm">{response.notes}</div>
                          </div>
                        )}
                        
                        {response.attachments && response.attachments.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium mb-1">Attachments</h4>
                            <div className="flex flex-wrap gap-2">
                              {response.attachments.map((attachment, index) => (
                                <Button 
                                  key={index} 
                                  variant="outline" 
                                  size="sm"
                                  asChild
                                >
                                  <a href={attachment} target="_blank" rel="noopener noreferrer">
                                    <FileTextIcon className="h-4 w-4 mr-1" />
                                    Document {index + 1}
                                  </a>
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
            
            {/* Specifications Tab */}
            <TabsContent value="specifications">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Specification</TableHead>
                      <TableHead>Your Request</TableHead>
                      {responses.map((response) => (
                        <TableHead key={response.id}>
                          {response.supplier.name}
                          {response.complianceScore !== undefined && (
                            <div className="text-xs font-normal">
                              Compliance: {response.complianceScore}%
                            </div>
                          )}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedSpecKeys.map((specKey) => (
                      <TableRow key={specKey}>
                        <TableCell className="font-medium">{specKey}</TableCell>
                        <TableCell>
                          {rfqSpecs[specKey] !== undefined 
                            ? String(rfqSpecs[specKey])
                            : "Not specified"
                          }
                        </TableCell>
                        {responses.map((response) => {
                          const specs = response.specifications_compliance || {};
                          const value = specs[specKey];
                          const requestedValue = rfqSpecs[specKey];
                          const isMatching = value === requestedValue;
                          
                          return (
                            <TableCell key={response.id}>
                              <div className="flex items-center gap-1">
                                {value !== undefined ? (
                                  <>
                                    <span>{String(value)}</span>
                                    {requestedValue !== undefined && (
                                      isMatching ? (
                                        <CheckCircleIcon className="h-4 w-4 text-green-500" />
                                      ) : (
                                        <AlertCircleIcon className="h-4 w-4 text-yellow-500" />
                                      )
                                    )}
                                  </>
                                ) : (
                                  <span className="text-muted-foreground">Not provided</span>
                                )}
                              </div>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
            
            {/* Pricing Tab */}
            <TabsContent value="pricing">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Base Price</TableHead>
                      <TableHead>Delivery Fee</TableHead>
                      <TableHead>Total Price</TableHead>
                      <TableHead>Price Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {responses.map((response) => {
                      const totalPrice = response.quoted_price + (response.delivery_fee || 0);
                      return (
                        <TableRow key={response.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                {response.supplier.logo ? (
                                  <AvatarImage src={response.supplier.logo} alt={response.supplier.name} />
                                ) : (
                                  <AvatarFallback>{response.supplier.name.charAt(0)}</AvatarFallback>
                                )}
                              </Avatar>
                              {response.supplier.name}
                            </div>
                          </TableCell>
                          <TableCell>{formatCurrency(response.quoted_price, response.currency)}</TableCell>
                          <TableCell>
                            {response.delivery_fee 
                              ? formatCurrency(response.delivery_fee, response.currency) 
                              : "Free delivery"
                            }
                          </TableCell>
                          <TableCell className="font-medium">
                            {formatCurrency(totalPrice, response.currency)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <div className={`font-medium ${getScoreColor(response.priceScore || 0)}`}>
                                {response.priceScore}%
                              </div>
                              {response.priceScore === Math.max(...responses.map(r => r.priceScore || 0)) && (
                                <Badge className="ml-2 bg-green-500">Best Price</Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
            
            {/* Delivery Tab */}
            <TabsContent value="delivery">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Supplier</TableHead>
                      <TableHead>Delivery Date</TableHead>
                      <TableHead>Your Requirement</TableHead>
                      <TableHead>Variance</TableHead>
                      <TableHead>Delivery Score</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {responses.map((response) => {
                      const deliveryDate = new Date(response.delivery_date);
                      const requiredDate = rfq.delivery_date_required 
                        ? new Date(rfq.delivery_date_required) 
                        : null;
                      
                      let daysDifference = null;
                      if (requiredDate) {
                        daysDifference = Math.ceil(
                          (deliveryDate.getTime() - requiredDate.getTime()) / (1000 * 60 * 60 * 24)
                        );
                      }
                      
                      return (
                        <TableRow key={response.id}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Avatar className="h-8 w-8">
                                {response.supplier.logo ? (
                                  <AvatarImage src={response.supplier.logo} alt={response.supplier.name} />
                                ) : (
                                  <AvatarFallback>{response.supplier.name.charAt(0)}</AvatarFallback>
                                )}
                              </Avatar>
                              {response.supplier.name}
                            </div>
                          </TableCell>
                          <TableCell>{formatDate(response.delivery_date)}</TableCell>
                          <TableCell>
                            {rfq.delivery_date_required 
                              ? formatDate(rfq.delivery_date_required)
                              : "Not specified"
                            }
                          </TableCell>
                          <TableCell>
                            {daysDifference !== null ? (
                              <div className="flex items-center">
                                {daysDifference <= 0 ? (
                                  <>
                                    <ArrowUpCircleIcon className="h-4 w-4 text-green-500 mr-1" />
                                    <span className="text-green-600">
                                      {daysDifference === 0 
                                        ? "On time" 
                                        : `${Math.abs(daysDifference)} days early`
                                      }
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    <ArrowDownCircleIcon className="h-4 w-4 text-red-500 mr-1" />
                                    <span className="text-red-600">
                                      {`${daysDifference} days late`}
                                    </span>
                                  </>
                                )}
                              </div>
                            ) : (
                              "N/A"
                            )}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <div className={`font-medium ${getScoreColor(response.deliveryScore || 0)}`}>
                                {response.deliveryScore}%
                              </div>
                              {response.deliveryScore === Math.max(...responses.map(r => r.deliveryScore || 0)) && (
                                <Badge className="ml-2 bg-green-500">Fastest</Badge>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
        <CardFooter className="flex justify-between border-t pt-6">
          <div className="text-sm text-muted-foreground">
            <span className="font-medium">{responses.length}</span> quotes received
          </div>
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            Export Comparison
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
